import { api } from '@shared/api';

export type PlanTier = 'DEMO' | 'PRO' | 'PREMIUM';

export interface PlanInfo {
  tier: PlanTier;
  title: string;
  /** 0 для бесплатного DEMO. */
  priceRub: number;
  dailyGenerationLimit: number;
  /** Это текущий тариф пользователя. */
  isCurrent: boolean;
  /** Цена перехода на этот тариф (апгрейд с активного платного: полная цена −
   *  зачёт неиспользованных дней). null — апгрейд неприменим, показываем priceRub. */
  upgradePriceRub: number | null;
}

export interface Quota {
  plan: PlanTier;
  dailyLimit: number;
  usedToday: number;
  remaining: number;
  /** Несгораемые жетоны: не обнуляются в полночь, тратятся, когда дневные закончились (с 2026-10-22). */
  bonus: number;
  /** ISO-8601 UTC — момент обнуления счётчика (00:00 МСК). Дневной для ВСЕХ тарифов,
   *  DEMO — бесплатно и без срока (с 2026-10-22). */
  resetAt: string;
}

// Бэк отдаёт snake_case, axios-интерцептор приводит ключи ответа к snake_case —
// здесь маппим в camelCase доменные типы (как в moduleApi).
interface BackendPlanInfo {
  tier: PlanTier;
  title: string;
  price_rub: number;
  daily_generation_limit: number;
  is_current: boolean;
  upgrade_price_rub: number | null;
}

interface BackendQuota {
  plan: PlanTier;
  daily_limit: number;
  used_today: number;
  remaining: number;
  // Появилось 2026-10-22: бэкенд до этой версии поле не отдаёт.
  bonus?: number;
  reset_at: string;
}

function mapQuota(q: BackendQuota): Quota {
  return {
    plan: q.plan,
    dailyLimit: q.daily_limit,
    usedToday: q.used_today,
    remaining: q.remaining,
    bonus: q.bonus ?? 0,
    resetAt: q.reset_at,
  };
}

export async function fetchPlans(): Promise<PlanInfo[]> {
  const { data } = await api.get<BackendPlanInfo[]>('/plans');
  return data.map((p) => ({
    tier: p.tier,
    title: p.title,
    priceRub: p.price_rub,
    dailyGenerationLimit: p.daily_generation_limit,
    isCurrent: p.is_current,
    upgradePriceRub: p.upgrade_price_rub,
  }));
}

export async function fetchQuota(): Promise<Quota> {
  const { data } = await api.get<BackendQuota>('/me/quota');
  return mapQuota(data);
}

export type PaymentStatus = 'PENDING' | 'SUCCEEDED' | 'CANCELED';
export type PaymentKind = 'SUBSCRIPTION' | 'DONATION';

export interface CreatePaymentResult {
  paymentId: string;
  /** URL страницы оплаты YooKassa — фронт делает на него full-page redirect. */
  confirmationUrl: string;
}

export interface PaymentStatusResult {
  paymentId: string;
  status: PaymentStatus;
  kind: PaymentKind;
  tier: PlanTier | null;
  /** Сколько списано — для чека на странице успеха. */
  amountRub: number;
}

export interface Subscription {
  plan: PlanTier;
  /** ISO-8601 — до какого момента активен платный тариф (null для DEMO). Без
   *  автопродления доступ просто заканчивается в эту дату. */
  planExpiresAt: string | null;
  priceRub: number;
}

interface BackendCreatePayment {
  payment_id: string;
  confirmation_url: string;
}

interface BackendPaymentStatus {
  payment_id: string;
  status: PaymentStatus;
  kind: PaymentKind;
  tier: PlanTier | null;
  amount_rub: number;
}

interface BackendSubscription {
  plan: PlanTier;
  plan_expires_at: string | null;
  price_rub: number;
}

/** Оплатить тариф — разовый доступ на 31 день (без автопродления). Возвращает
 *  URL для редиректа на страницу оплаты YooKassa. */
export async function createPayment(tier: PlanTier): Promise<CreatePaymentResult> {
  const { data } = await api.post<BackendCreatePayment>('/payments', { tier });
  return { paymentId: data.payment_id, confirmationUrl: data.confirmation_url };
}

/** Создать платёж-донат на указанную сумму (₽). Возвращает URL для редиректа. */
export async function createDonation(amountRub: number): Promise<CreatePaymentResult> {
  // Тело запроса не проходит camel→snake-трансформер — отправляем snake-ключ.
  const { data } = await api.post<BackendCreatePayment>('/payments/donate', {
    amount_rub: amountRub,
  });
  return { paymentId: data.payment_id, confirmationUrl: data.confirmation_url };
}

/** Сверить статус платежа с YooKassa (вызывает страница /payment/success). */
export async function syncPaymentStatus(paymentId: string): Promise<PaymentStatusResult> {
  const { data } = await api.post<BackendPaymentStatus>(`/payments/${paymentId}/sync`);
  return {
    paymentId: data.payment_id,
    status: data.status,
    kind: data.kind,
    tier: data.tier,
    amountRub: data.amount_rub,
  };
}

function mapSubscription(d: BackendSubscription): Subscription {
  return {
    plan: d.plan,
    planExpiresAt: d.plan_expires_at,
    priceRub: d.price_rub,
  };
}

/** Сводка по тарифу (какой план, до какого числа активен, цена). */
export async function fetchSubscription(): Promise<Subscription> {
  const { data } = await api.get<BackendSubscription>('/payments/subscription');
  return mapSubscription(data);
}

// ============================================================
// Rolling 31-day billing (GET /billing/overview, POST /billing/invoice)
//
// Период = [start, start + 31 день) от даты покупки. Календарных месяцев больше
// нет: есть ровно два слота — ТЕКУЩИЙ период и СЛЕДУЮЩИЙ (предоплачиваемый).
// ============================================================

/** Слот периода (current или next). Даты — явные ISO-UTC диапазоны. */
export interface PeriodView {
  plan: PlanTier; // план периода; DEMO = не оплачен
  paid: boolean; // есть оплаченный период
  isCurrent: boolean;
  payable: boolean; // можно купить этот слот как новый период по полной цене
  canUpgrade: boolean; // можно апгрейднуть Pro→Premium на этом слоте
  upgradePay: number | null; // цена апгрейда (если canUpgrade)
  startsAt: string; // ISO-8601 UTC — начало периода
  endsAt: string | null; // ISO-8601 UTC — конец периода (эксклюзивный); у DEMO — null: без срока
  daysLeft: number | null; // целых дней до конца оплаченного периода; у DEMO — null
}

/** Прошлый оплаченный период для блока «История». */
export interface HistoryRow {
  startsAt: string;
  endsAt: string;
  plan: PlanTier;
  amountRub: number;
}

/**
 * Успешная оплата тарифа для «Истории оплат» — по строке на каждый платёж.
 * Доплата Pro → Max идёт отдельной строкой (в HistoryRow она сливается с
 * покупкой: апгрейд обновляет строку периода).
 */
export interface PlanPaymentRow {
  createdAt: string; // ISO-8601 UTC — когда оформлен платёж
  kind: 'purchase' | 'upgrade'; // покупка периода / доплата внутри него
  plan: PlanTier; // оплаченный тариф (у доплаты — новый)
  fromPlan: PlanTier | null; // доплата: тариф, с которого перешли
  amountRub: number;
  startsAt: string | null; // оплаченный период; null у legacy-платежей
  endsAt: string | null;
}

/** Успешный донат для «Истории оплат» (периодом не является). */
export interface DonationRow {
  createdAt: string;
  amountRub: number;
}

/** Цены и дневные лимиты тарифов (из конфига бэка). */
export interface BillingPrices {
  proRub: number;
  premiumRub: number;
  demoDaily: number; // жетонов в день у DEMO (без срока)
  proDaily: number;
  premiumDaily: number;
}

/** Единый payload страницы «Тарифы». */
export interface BillingOverview {
  currentPlan: PlanTier;
  quota: Quota;
  /** Текущий период — присутствует ВСЕГДА. */
  current: PeriodView;
  /** Следующий период — null на Demo (нет активного периода, предоплатить нельзя). */
  next: PeriodView | null;
  prices: BillingPrices;
  periodDays: number; // длина периода в днях (30)
  history: HistoryRow[]; // прошлые периоды, новые сверху
  planPayments: PlanPaymentRow[]; // оплаты тарифа по платежам, новые сверху
  donations: DonationRow[]; // успешные донаты, новые сверху
  donatePresets: number[];
  donateMin: number;
}

interface BackendPeriodView {
  plan: PlanTier;
  paid: boolean;
  is_current: boolean;
  payable: boolean;
  can_upgrade: boolean;
  upgrade_pay: number | null;
  starts_at: string;
  ends_at: string | null;
  days_left: number | null;
}

interface BackendBillingOverview {
  current_plan: PlanTier;
  quota: BackendQuota;
  current: BackendPeriodView;
  next: BackendPeriodView | null;
  prices: {
    pro_rub: number;
    premium_rub: number;
    demo_daily: number;
    pro_daily: number;
    premium_daily: number;
  };
  period_days: number;
  history: { starts_at: string; ends_at: string; plan: PlanTier; amount_rub: number }[];
  // Появились 2026-09-23: бэкенд до этой версии поля не отдаёт.
  plan_payments?: BackendPlanPaymentRow[];
  donations?: { created_at: string; amount_rub: number }[];
  donate_presets: number[];
  donate_min: number;
}

interface BackendPlanPaymentRow {
  created_at: string;
  kind: 'purchase' | 'upgrade';
  plan: PlanTier;
  from_plan?: PlanTier | null;
  amount_rub: number;
  starts_at: string | null;
  ends_at: string | null;
}

function mapPlanPayment(p: BackendPlanPaymentRow): PlanPaymentRow {
  return {
    createdAt: p.created_at,
    kind: p.kind,
    plan: p.plan,
    fromPlan: p.from_plan ?? null,
    amountRub: p.amount_rub,
    startsAt: p.starts_at,
    endsAt: p.ends_at,
  };
}

function mapPeriodView(p: BackendPeriodView): PeriodView {
  return {
    plan: p.plan,
    paid: p.paid,
    isCurrent: p.is_current,
    payable: p.payable,
    canUpgrade: p.can_upgrade,
    upgradePay: p.upgrade_pay,
    startsAt: p.starts_at,
    endsAt: p.ends_at,
    daysLeft: p.days_left,
  };
}

/** Единый payload страницы «Тарифы» (rolling 31-day-модель периодов). */
export async function fetchBillingOverview(): Promise<BillingOverview> {
  const { data } = await api.get<BackendBillingOverview>('/billing/overview');
  return {
    currentPlan: data.current_plan,
    quota: mapQuota(data.quota),
    current: mapPeriodView(data.current),
    next: data.next ? mapPeriodView(data.next) : null,
    prices: {
      proRub: data.prices.pro_rub,
      premiumRub: data.prices.premium_rub,
      demoDaily: data.prices.demo_daily,
      proDaily: data.prices.pro_daily,
      premiumDaily: data.prices.premium_daily,
    },
    periodDays: data.period_days,
    history: data.history.map((h) => ({
      startsAt: h.starts_at,
      endsAt: h.ends_at,
      plan: h.plan,
      amountRub: h.amount_rub,
    })),
    // Старый бэкенд без plan_payments: строка на период, как было раньше.
    planPayments: (
      data.plan_payments ??
      data.history.map((h) => ({
        created_at: h.starts_at,
        kind: 'purchase' as const,
        plan: h.plan,
        amount_rub: h.amount_rub,
        starts_at: h.starts_at,
        ends_at: h.ends_at,
      }))
    ).map(mapPlanPayment),
    donations: (data.donations ?? []).map((d) => ({
      createdAt: d.created_at,
      amountRub: d.amount_rub,
    })),
    donatePresets: data.donate_presets,
    donateMin: data.donate_min,
  };
}

/** Какой слот периода оплачиваем: текущий или следующий. */
export type PeriodSlot = 'current' | 'next';

export interface CreateInvoiceArgs {
  tier: Extract<PlanTier, 'PRO' | 'PREMIUM'>;
  slot: PeriodSlot;
}

/** Выставить счёт на слот периода (новый период по полной цене либо апгрейд
 *  Pro→Premium). Возвращает URL для редиректа на YooKassa. На запрещённую
 *  комбинацию слот/тариф бэк отвечает 400 INVALID_PAYMENT_TIER. */
export async function createInvoice({
  tier,
  slot,
}: CreateInvoiceArgs): Promise<CreatePaymentResult> {
  // Тело запроса не проходит camel→snake-трансформер — отправляем как есть.
  const { data } = await api.post<BackendCreatePayment>('/billing/invoice', {
    tier,
    slot,
  });
  return { paymentId: data.payment_id, confirmationUrl: data.confirmation_url };
}
